import React from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

export type GenrateCertificateProps = {
  visible: boolean;
  onHide: () => void;
  insuredDatabaseId?: string;
};

const GenrateCertificate: React.FC<GenrateCertificateProps> = ({ visible, onHide, insuredDatabaseId }) => {
  const toastRef = React.useRef<any>(null);
  const [certificate, setCertificate] = React.useState<string | null>(null);
  const [masterCertificateOptions, setMasterCertificateOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [masterCertificatesRaw, setMasterCertificatesRaw] = React.useState<any[]>([]);
  const [loadingMaster, setLoadingMaster] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [additionalInterest, setAdditionalInterest] = React.useState<string[]>([]);
  const [additionalInterestOptions, setAdditionalInterestOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [additionalInterestsRaw, setAdditionalInterestsRaw] = React.useState<any[]>([]);
  const [loadingInterest, setLoadingInterest] = React.useState(true);
  const [interestError, setInterestError] = React.useState<string | null>(null);
  const [downloadOption, setDownloadOption] = React.useState<string>('pdf');
  const downloadOptions = [
    { label: "All certificates in a single .pdf", value: "pdf" },
    { label: "All certificates in a .zip", value: "zip" },
  ];
  const [saveOption, setSaveOption] = React.useState<string | null>(null);
  const saveOptions = [
    { label: "All certificates in a single .pdf", value: "pdf" },
    { label: "All certificates in a .zip", value: "zip" },
  ];
  const [generateHistorical, setGenerateHistorical] = React.useState(false);
  const [generating, setGenerating] = React.useState(false);
  const [pdfUrls, setPdfUrls] = React.useState<{ previewUrl: string; downloadUrl: string } | null>(null);

  React.useEffect(() => {
    if (!visible) {
      setCertificate(null);
      setAdditionalInterest([]);
      setDownloadOption('pdf');
      setSaveOption(null);
      setGenerateHistorical(false);
      setPdfUrls(null);
    }
  }, [visible]);

  React.useEffect(() => {
    if (!visible || !insuredDatabaseId) {
      setLoadingMaster(false);
      return;
    }
    setLoadingMaster(true);
    setError(null);
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/master-certificates?insuredDatabaseId=${insuredDatabaseId}`, {
      headers: {
        'NCToken': ncToken,
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    })
      .then(res => res.ok ? res : Promise.reject(res))
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.value)) {
          const activeCerts = data.value.filter((cert: any) => cert.active !== false);
          setMasterCertificatesRaw(activeCerts);
          setMasterCertificateOptions(activeCerts.map((cert: any) => ({ label: cert.name, value: cert.databaseId })));
        } else {
          setMasterCertificatesRaw([]);
          setMasterCertificateOptions([]);
        }
      })
      .catch(() => setError('Failed to load master certificates'))
      .finally(() => setLoadingMaster(false));
  }, [visible, insuredDatabaseId]);

  React.useEffect(() => {
    if (!visible || !insuredDatabaseId) {
      setLoadingInterest(false);
      return;
    }
    setLoadingInterest(true);
    setInterestError(null);
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/policy-additional-interests?insuredDatabaseId=${encodeURIComponent(insuredDatabaseId)}&top=100`, {
      headers: {
        'NCToken': ncToken,
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    })
      .then(res => res.ok ? res : Promise.reject(res))
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.value)) {
          const active = data.value.filter((item: any) => item.active);
          setAdditionalInterestsRaw(active);
          setAdditionalInterestOptions(active.map((item: any) => ({ label: item.certificateHolderName, value: item.databaseId })));
        } else {
          setAdditionalInterestsRaw([]);
          setAdditionalInterestOptions([]);
        }
      })
      .catch(() => setInterestError('Failed to load additional interests'))
      .finally(() => setLoadingInterest(false));
  }, [visible, insuredDatabaseId]);

  // Filter additional interests to only those matching the selected master cert's policies
  React.useEffect(() => {
    const ncUserId = localStorage.getItem('NCUserId');
    setAdditionalInterest([]);
    if (!certificate) {
      const filtered = additionalInterestsRaw.filter((item: any) => item.lastChangeUserId === ncUserId);
      setAdditionalInterestOptions(filtered.map((item: any) => ({ label: item.certificateHolderName, value: item.databaseId })));
      return;
    }
    const mc = masterCertificatesRaw.find((c: any) => c.databaseId === certificate);
    const mcPolicyIds: string[] = mc?.policies?.map((p: any) => p.databaseId as string) ?? [];
    if (mcPolicyIds.length === 0) {
      const filtered = additionalInterestsRaw.filter((item: any) => item.lastChangeUserId === ncUserId);
      setAdditionalInterestOptions(filtered.map((item: any) => ({ label: item.certificateHolderName, value: item.databaseId })));
      return;
    }
    const filtered = additionalInterestsRaw.filter((item: any) =>
      (item.policies ?? []).some((p: any) => mcPolicyIds.includes(p.databaseId))
    );
    setAdditionalInterestOptions(filtered.map((item: any) => ({ label: item.certificateHolderName, value: item.databaseId })));
  }, [certificate, masterCertificatesRaw, additionalInterestsRaw]);

  const handleGenerateCertificate = async () => {
    if (!insuredDatabaseId || !certificate) {
      toastRef.current?.show({ severity: 'error', summary: 'Missing Data', detail: 'Please select a master certificate.', life: 2500 });
      return;
    }
    if (!additionalInterest.length) {
      toastRef.current?.show({ severity: 'error', summary: 'Missing Data', detail: 'Please select at least one additional interest.', life: 2500 });
      return;
    }
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    const payload = {
      insuredDatabaseId,
      certificateId: certificate,
      additionalInterestIds: additionalInterest,
      generateHistoricalCertificates: generateHistorical,
      DownloadOptions: generateHistorical ? '' : downloadOption.toUpperCase(),
      SaveOptions: '',
    };
    setGenerating(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/generate-certificate`, {
        method: 'POST',
        headers: {
          'NCToken': ncToken,
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to generate certificate');

      const json = await res.json().catch(() => null);

      if (json?.previewUrl || json?.downloadUrl) {
        // PDF/ZIP case — show preview dialog
        setPdfUrls({ previewUrl: json.previewUrl || '', downloadUrl: json.downloadUrl || '' });
      } else {
        // Historical JSON case — databaseIds returned
        const msg: string = json?.message || 'Certificate(s) generated successfully.';
        toastRef.current?.show({ severity: 'success', summary: 'Generated', detail: msg, life: 3000 });
        setTimeout(() => onHide(), 800);
      }
    } catch (err) {
      toastRef.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to generate certificate.', life: 3000 });
    } finally {
      setGenerating(false);
    }
  };

  const handleClosePdfPreview = () => {
    setPdfUrls(null);
    onHide();
  };

  return (
    <>
      <Dialog
        header={<span style={{ color: '#ba170b', fontWeight: 600 }}>Generate Certificate</span>}
        visible={visible && !pdfUrls}
        style={{ width: "500px", maxWidth: "98vw" }}
        onHide={onHide}
        modal
        className="addCertHolderDialog"
      >
        <div className="pageCard" style={{ marginBottom: 12, padding: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>
                Certificate <span style={{ color: 'red', marginLeft: 2 }}>*</span>
              </div>
              {error ? (
                <div style={{ color: 'red' }}>{error}</div>
              ) : (
                <Dropdown
                  value={certificate}
                  options={masterCertificateOptions}
                  onChange={e => setCertificate(e.value)}
                  placeholder={loadingMaster ? "Loading..." : "Select master certificate"}
                  style={{ width: '100%' }}
                  disabled={loadingMaster}
                  emptyMessage="No available options"
                />
              )}
            </div>
            <div>
              <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>
                Additional Interest <span style={{ color: 'red' }}>*</span>
              </div>
              {interestError ? (
                <div style={{ color: 'red' }}>{interestError}</div>
              ) : (
                <MultiSelect
                  value={additionalInterest}
                  options={additionalInterestOptions}
                  onChange={e => setAdditionalInterest(e.value)}
                  placeholder={loadingInterest ? "Loading..." : "Select additional interest"}
                  style={{ width: '100%' }}
                  disabled={loadingInterest}
                  emptyMessage="No available options"
                  className="cert-holder-ms"
                  display="chip"
                  appendTo={document.body}
                />
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                inputId="generateHistorical"
                checked={generateHistorical}
                onChange={e => setGenerateHistorical(e.checked ?? false)}
              />
              <label htmlFor="generateHistorical" style={{ color: '#0057A4', fontWeight: 500, marginLeft: 8 }}>
                Generate historical certificates for every additional interest
              </label>
            </div>
            {generateHistorical && (
              <div>
                <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>Save options</div>
                <Dropdown
                  value={saveOption}
                  options={saveOptions}
                  onChange={e => setSaveOption(e.value)}
                  placeholder="Please select save option"
                  style={{ width: '100%' }}
                  emptyMessage="No available options"
                />
              </div>
            )}
            <div>
              <div style={{ color: '#0057A4', fontWeight: 500, marginBottom: 4 }}>Download Options</div>
              <Dropdown
                value={downloadOption}
                options={downloadOptions}
                onChange={e => setDownloadOption(e.value)}
                placeholder="Please select download option"
                style={{ width: '100%' }}
                emptyMessage="No available options"
              />
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={onHide}
            style={{ color: '#002A5C', fontWeight: 600 }}
          />
          <Button
            label={generating ? 'Generating...' : 'Generate Certificate'}
            icon={generating ? 'pi pi-spin pi-spinner' : undefined}
            className="p-button-primary"
            onClick={handleGenerateCertificate}
            disabled={generating || !certificate || !additionalInterest.length}
            style={{ background: '#0057A4', borderColor: '#0057A4', color: '#fff', fontWeight: 600 }}
          />
        </div>
        <Toast ref={toastRef} position="top-center" />
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog
        header={<span style={{ color: '#ba170b', fontWeight: 600 }}>Certificate Preview</span>}
        visible={!!pdfUrls}
        style={{ width: '85vw', maxWidth: '1100px' }}
        onHide={handleClosePdfPreview}
        modal
      >
        {pdfUrls?.previewUrl && (
          <iframe
            src={pdfUrls.previewUrl}
            title="Certificate preview"
            style={{ width: '100%', height: '75vh', border: 'none', display: 'block' }}
          />
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          {pdfUrls?.downloadUrl && (
            <Button
              label="Download"
              icon="pi pi-download"
              className="p-button-primary"
              onClick={() => window.open(pdfUrls.downloadUrl, '_blank')}
              style={{ background: '#0057A4', borderColor: '#0057A4', color: '#fff', fontWeight: 600 }}
            />
          )}
          <Button
            label="Close"
            className="p-button-text"
            onClick={handleClosePdfPreview}
            style={{ color: '#002A5C', fontWeight: 600 }}
          />
        </div>
      </Dialog>
    </>
  );
};

export default GenrateCertificate;
